import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';

enum cookieValueAgeVerified {
    TRUE = 'true',
    FALSE = 'false'
}

export interface IAgeGateState {
    isVisible: boolean;
}

interface IAgeGateExternalProps {
    externalIsOpen?: boolean;
    onCloseExternallyTriggered?: () => void;
    context?: any;
    triggeredFrom?: string;
}

class AgeGate extends React.PureComponent<IAgeGateExternalProps, IAgeGateState> {
    constructor(props: IAgeGateExternalProps) {
        super(props);
        // Initialize isVisible based on external prop or internal cookie check
        this.state = {
            isVisible: props.externalIsOpen || !this._isActionTaken(this._getCookieName())
        };
    }

    public componentDidMount(): void {
        // If externalIsOpen is provided and true, we don't need to check cookie on mount
        if (this.props.externalIsOpen) {
            this.setState({ isVisible: true });
        } else {
            // Original cookie check logic
            if (!this._isActionTaken(this._getCookieName())) {
                this.setState({ isVisible: true });
            }
        }
    }

    // Add componentDidUpdate to react to changes in externalIsOpen prop
    public componentDidUpdate(prevProps: IAgeGateExternalProps): void {
        if (this.props.externalIsOpen !== prevProps.externalIsOpen) {
            this.setState({ isVisible: !!this.props.externalIsOpen });
        }
    }

    private _getCookieName = (): string => {
        return 'ageGateCookie';
    };

    public render(): JSX.Element | null {
        if (!this.state.isVisible) {
            return null;
        }

        return (
            <div className='modal-container'>
                <div className='modal-dialog'>
                    <div className='modal-body'>
                        <p>We are required by law to confirm your age before viewing restricted products.</p>
                    </div>
                    <div className='modal-actions'>
                        <button type='button' className='yes-button' onClick={this._handleConsent}>
                            I am aged 18 or over
                        </button>
                        <button type='button' className='no-button' onClick={this._handleUnderage}>
                            I am under 18 years old
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * _isActionTaken method - To check cookie value.
     * @param cookieName - Cookie name.
     * @returns - Boolean value for existence of cookie or it's value itself.
     */
    private readonly _isActionTaken = (cookieName: string): boolean => {
        const cookies = this.props.context.request.cookies;
        const ageGateCookie = cookies.get(cookieName) as Msdyn365.ICookieValue<{}> | undefined;

        if (!ageGateCookie || ageGateCookie.value === cookieValueAgeVerified.FALSE) {
            return false;
        }

        return ageGateCookie.value === cookieValueAgeVerified.TRUE; // Ensure it's explicitly 'true'
    };

    private readonly _handleConsent = (): void => {
        const cookieName = this._getCookieName();
        const { cookies } = this.props.context.request;

        if (cookies.isConsentGiven()) {
            // Set consent cookie
            cookies?.set(cookieName, cookieValueAgeVerified.TRUE);
        }

        // Hide the modal and trigger parent's callback
        this.setState(
            {
                isVisible: false
            },
            () => {
                this.props.onCloseExternallyTriggered?.();
            }
        );
    };

    // NEW: Handler for when the user declares they are underage
    private readonly _handleUnderage = (): void => {
        const cookieName = this._getCookieName();
        const { cookies } = this.props.context.request;

        if (cookies.isConsentGiven()) {
            // 1. Set a cookie indicating rejection
            cookies?.set(cookieName, cookieValueAgeVerified.FALSE);
        }

        if (this.props.triggeredFrom?.toLowerCase() === 'navigation-menu') {
            this.setState(
                {
                    isVisible: false
                },
                () => {
                    this.props.onCloseExternallyTriggered?.();
                }
            );
        } else {
            // Navigate back in browser history
            if (typeof window !== 'undefined' && window.history) {
                window.history.back();
            } else {
                console.warn("window.history is not available for 'goBack' action.");
                // Fallback for SSR or environments without history
                // You might choose to set isVisible to false here, but it's not ideal.
                this.setState({ isVisible: false });
            }
        }
    };
}

export default AgeGate;
